/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ROUTES, MONITOR_PAGE_URLS } from '../../../Common/constants/constants';

import { getLocationResources } from 'LocationResourcesAPI';
import { RESOURCE_TYPES } from 'dataConstants';
import { getAssetResources } from 'AssetResourcesAPI';

interface ResourceDrilldownLinkType {
  type: string;
  uuid: string;
  value: string;
  deviceTypeName: string | undefined;
  deviceName: string | undefined;
  userEmailId: string;
}

/**
 * This component returns a link to a resource dashboard for a given alert
 * It needs to handle constructing the URL based on an API fetch to the resource
 */
const ResourceDrilldownLink = ({
  type,
  uuid,
  value,
  deviceTypeName,
  deviceName,
  userEmailId,
}: ResourceDrilldownLinkType): JSX.Element => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);

  const handleNavigate = useCallback((): void => {
    // Only fetch if we're not already fetching
    if (!isLoading) {
      if (type === RESOURCE_TYPES.LOCATION) {
        setIsLoading(true);
        getLocationResources({ userEmailId, filters: { uuid } })
          .then(({ results }) => {
            setIsLoading(false);
            history.push({
              pathname: `/monitor/sites/${results[0]?.siteUUId}/locations/${uuid}/${MONITOR_PAGE_URLS.DASHBOARDS}`,
              state: {
                resourceType: RESOURCE_TYPES.LOCATION,
                resourceUUId: uuid,
              },
            });
          })
          .catch(err => {
            console.error(err);
            setIsLoading(false);
          });
      } else if (type === RESOURCE_TYPES.ASSET) {
        setIsLoading(true);
        getAssetResources({ userEmailId, filters: { uuid } })
          .then(({ results }) => {
            setIsLoading(false);
            history.push({
              pathname: `/monitor/sites/${results[0]?.siteUUId}/${MONITOR_PAGE_URLS.ASSETS}/${uuid}/${MONITOR_PAGE_URLS.DASHBOARDS}`,
              state: { resourceType: RESOURCE_TYPES.ASSET, resourceUUId: uuid },
            });
          })
          .catch(err => {
            console.error(err);
            setIsLoading(false);
          });
      } else if (type === RESOURCE_TYPES.DEVICE_TYPE || type === RESOURCE_TYPES.DEVICE) {
        if (!deviceName) {
          history.push({
            pathname: `${ROUTES.MONITOR_DEVICE_TYPE}/${deviceTypeName}/${MONITOR_PAGE_URLS.DASHBOARDS}`,
            state: { resourceType: RESOURCE_TYPES.DEVICE_TYPE, resourceUUId: uuid },
          });
        } else {
          history.push({
            pathname: `${ROUTES.MONITOR_DEVICE_TYPE}/${deviceTypeName}/${MONITOR_PAGE_URLS.DEVICES}/${deviceName}/${MONITOR_PAGE_URLS.DASHBOARDS}`,
            state: { resourceType: RESOURCE_TYPES.DEVICE, resourceUUId: uuid },
          });
        }
      }
    }
  }, [deviceName, deviceTypeName, history, isLoading, type, userEmailId, uuid]);

  return (
    <span
      className="resource-drilldown-link"
      role="button"
      tabIndex={0}
      onKeyDown={handleNavigate}
      onClick={handleNavigate}>
      {value}
    </span>
  );
};

export default ResourceDrilldownLink;
